import { useContext, useEffect, useMemo } from 'react';
import { KeyActionContext } from './KeyActionProvider';
import { type KeyAction } from './types';

/**
 * React hook for setting up keyboard actions.
 *
 * @param actions - An array of key actions.
 *   Note that if this array changes, we'll remove and re-add all the actions
 *   from the global key registry.  You can avoid this by using the idiom
 *   useKeyActions(useMemo(() => ..., deps))` where `deps` are values used by
 *   your actions, or alternatively by using a constant array for actions.
 *
 * When `KeyActionsProvider` has `shortcutsDisabled`, no shortcuts are registered.
 */
export function useKeyActions(actions: KeyAction[]) {
  const ctx = useContext(KeyActionContext);
  if (!ctx.actionsRegistry) {
    throw new Error(`Missing KeyActionProvider.`);
  }
  const actionsRegistry = ctx.actionsRegistry;
  const shortcutsDisabled = ctx.shortcutsDisabled ?? false;

  // Filter out actions that are `visible: false`, and skip all when shortcuts are disabled.
  const availableActions = useMemo(() => {
    if (shortcutsDisabled) {
      return [];
    }
    return actions.filter((action) => action.visible ?? true);
  }, [actions, shortcutsDisabled]);

  // Register actions with the actionsRegistry.
  useEffect(() => {
    actionsRegistry.registerActions(availableActions);
    return () => actionsRegistry.deregisterActions(availableActions);
  }, [actionsRegistry, availableActions]);
}
